import type {
  VideoModelType,
  VideoDurationSeconds,
} from '../../services/api/videoGenerationService';

/**
 * 動画生成モデルのカタログ。
 *
 * ここが「モデルごとに何ができるか」の唯一の真実。以前は VideoGenerationTab の中に
 * `MODELS_WITHOUT_1080P` / `isSeedanceModel` / `durationOptions` / `audioAlwaysOn` として
 * 分散しており、モデルを1つ足すたびに4箇所を直す必要があった（＝どこか1つを忘れると
 * 「選べるのに API が弾く」形で表面化する）。表示と制約を同じ場所から引くことで、
 * モーダルのスペック表示と実際に送れる値が食い違わないようにしている。
 *
 * 消費クレジットは意図的に持たせていない。実額は provider・モデル・尺・解像度に加えて
 * 管理画面のキャンペーン割引（CreditCalculationService::videoMultiplier）の関数で、
 * 数値をここに焼くと割引期間中に嘘の表示になる。相対的な costTier だけを持つ。
 */

/**
 * 相対的な消費クレジット感。実額は持たない（キャンペーン割引で変わるため）。
 *
 * 課金方式がモデルで違うので、揃えないと比較できない:
 *   Seedance = トークン課金（秒数 × 解像度別トークン数 × $/1M）
 *   MiniMax / Veo = 秒単価
 * config/credits.php から 720p の 1 秒あたりクレジットに換算した実測値
 * （credits = USD × rate_per_usd 750。5 秒生成の実消費と完全一致を確認済み）:
 *
 *   Seedance 2.0 Mini   56.7 cr/s   ← low
 *   MiniMax H3          60.0 cr/s   ← low
 *   Seedance 2.0 Fast   90.7 cr/s   ← standard
 *   Seedance 2.0 Pro   113.4 cr/s   ← standard
 *   Seedance 2.5       173.3 cr/s   ← high
 *   Gemini (Veo)       300.0 cr/s   ← high（Mini の 5.3 倍で最も高い）
 *
 * 単価を変えたらここも見直すこと。参照元は config/credits.php の
 * video_token_rates / video_rates / video_tokens_per_second。
 */
export type VideoModelCostTier = 'low' | 'standard' | 'high';
export type VideoModelAudio = 'always' | 'optional';
export type VideoAspectRatio = '16:9' | '9:16' | '1:1';
export type VideoResolution = '720p' | '1080p';

export interface VideoModelSpec {
  id: VideoModelType;
  label: string;
  /** カードに出す一言。従来の <option> ラベルの括弧書きに相当する。 */
  tagline: string;
  /** 相対的な消費クレジット感。実額ではない。 */
  costTier: VideoModelCostTier;
  /** 選べる尺。並び順がそのまま UI の並び順になる。 */
  durations: VideoDurationSeconds[];
  supports1080p: boolean;
  supportsSquare: boolean;
  /**
   * 音声の扱い。'optional' は生成時のトグルで ON/OFF できる、
   * 'always' は常に音声つきで OFF にできない。
   * 音声を出せること自体は全モデル共通なので、区別すべきは「切れるかどうか」。
   * 実測(2026-08-30): Seedance 2.0 Mini は generate_audio=true で aac が入り、
   * false なら無音。MiniMax H3 は false を送っても aac が入る。
   */
  audio: VideoModelAudio;
  /** 解像度セレクトの説明文。MiniMax H3 だけ実際の出力が 768P / 2K なので上書きする。 */
  resolutionDescriptions: Record<VideoResolution, string>;
  /**
   * プロバイダが受け付けるプロンプトの長さ（**文字数**。バイト数ではない）。
   * 上限が判明していないモデルは null にする。憶測の値を入れると、
   * 実際には通る長さを UI が勝手に止めてしまう。
   *
   * MiniMax H3 は 7,000 文字（公式: "counted by characters"）。超えると
   * task 作成が 400 / code 2013 `content[0].text too long` で落ちる。
   */
  promptMaxChars: number | null;
  /**
   * 選ぶ前に知っておくべき制約を1行で。カードに常時出す。
   *
   * 本番30日の動画広告の失敗 72 件のうち 26 件が「実人物が写る参照画像」の拒否で、
   * 選択時点で読めていれば避けられたもの。tagline（売り文句）とは役割を分ける。
   */
  note: string;
  /**
   * サンプル素材。まだ用意できていないモデルは null。
   * 必須にすると、素材が撮れないモデル（Seedance 1.5 Pro は生成基盤側で失敗する）や
   * アップロード漏れ・S3 の 404 で、カード全体が壊れた見た目になる。
   */
  sample: { video: string; poster: string } | null;
}

/** 尺のラベルはモデルによらず共通。 */
const DURATION_LABELS: Record<number, { label: string; description: string }> = {
  5: { label: '5秒', description: 'SNS広告向け短尺' },
  6: { label: '6秒', description: 'バンパー広告' },
  8: { label: '8秒', description: '標準広告（推奨）' },
  12: { label: '12秒', description: 'ストーリー広告' },
  15: { label: '15秒', description: '最長ストーリー' },
  20: { label: '20秒', description: '2.5 のみ・消費クレジット大' },
  25: { label: '25秒', description: '2.5 のみ・消費クレジット大' },
  30: { label: '30秒', description: '2.5 最大尺・消費クレジット最大' },
};

const GENERIC_RESOLUTION_DESCRIPTIONS: Record<VideoResolution, string> = {
  '720p': '高速生成、標準品質',
  '1080p': '高品質、YouTube推奨',
};

const ASPECT_RATIO_LABELS: Record<
  VideoAspectRatio,
  { label: string; description: string }
> = {
  '16:9': { label: '横長 (16:9)', description: 'YouTube、一般的な動画向け' },
  '9:16': { label: '縦長 (9:16)', description: 'TikTok、Instagram Reels向け' },
  '1:1': { label: '正方形 (1:1)', description: 'Instagram フィード、バナー向け' },
};

/**
 * サンプル素材のパス。`public/model-samples/` に置いた静的ファイルで、
 * ファイル名の `-v1` はデプロイのたびに走る CloudFront の `--paths "/*"` 無効化と
 * ブラウザ側の immutable キャッシュを両立させるためのもの。素材を差し替えるときは
 * 上書きせず `-v2` にリネームする（上書きすると古いキャッシュを持つ利用者に
 * 1年間古い動画が出続ける）。
 */
const sample = (id: string, version = 1) => ({
  video: `/model-samples/${id}-v${version}.mp4`,
  poster: `/model-samples/${id}-v${version}.jpg`,
});

export const VIDEO_MODEL_CATALOG: VideoModelSpec[] = [
  {
    id: 'seedance-2-mini',
    label: 'Seedance 2.0 Mini',
    tagline: '迷ったらこれ。低コストで一通りの表現ができる標準モデル',
    note: '実在の人物が写る参照画像は使えません',
    costTier: 'low',
    durations: [5, 8, 12],
    supports1080p: false,
    supportsSquare: true,
    audio: 'optional',
    resolutionDescriptions: GENERIC_RESOLUTION_DESCRIPTIONS,
    promptMaxChars: null,
    sample: sample('seedance-2-mini'),
  },
  {
    id: 'seedance-2-fast',
    label: 'Seedance 2.0 Fast',
    tagline: '仕上がりより速さ優先。案出しを数多く回したいとき向き',
    note: '実在の人物が写る参照画像は使えません',
    costTier: 'standard',
    durations: [5, 8, 12],
    supports1080p: false,
    supportsSquare: true,
    audio: 'optional',
    resolutionDescriptions: GENERIC_RESOLUTION_DESCRIPTIONS,
    promptMaxChars: null,
    sample: sample('seedance-2-fast'),
  },
  {
    id: 'seedance-2-pro',
    label: 'Seedance 2.0 Pro',
    tagline: '高画質で1080pに対応。本番入稿するならこれ',
    note: '実在の人物が写る参照画像は使えません',
    costTier: 'standard',
    durations: [5, 8, 12, 15],
    supports1080p: true,
    supportsSquare: true,
    audio: 'optional',
    resolutionDescriptions: GENERIC_RESOLUTION_DESCRIPTIONS,
    promptMaxChars: null,
    sample: sample('seedance-2-pro'),
  },
  {
    id: 'seedance-2-5',
    label: 'Seedance 2.5',
    tagline: '最大30秒。ストーリー性のある長尺を作れる代わりに高コスト',
    note: '実在の人物が写る参照画像は使えません',
    costTier: 'high',
    durations: [5, 8, 12, 15, 20, 25, 30],
    supports1080p: false,
    supportsSquare: true,
    audio: 'optional',
    resolutionDescriptions: GENERIC_RESOLUTION_DESCRIPTIONS,
    promptMaxChars: null,
    sample: sample('seedance-2-5'),
  },
  {
    id: 'minimax-h3',
    label: 'MiniMax H3',
    tagline: '音声が必ず付く（OFF にできない）。安く、最大15秒',
    note: '人物の参照画像に対応',
    costTier: 'low',
    durations: [5, 8, 12, 15],
    supports1080p: true,
    supportsSquare: true,
    audio: 'always',
    resolutionDescriptions: {
      '720p': '768Pで生成、標準品質',
      '1080p': '2K相当で生成、消費クレジット大',
    },
    promptMaxChars: 7000,
    sample: sample('minimax-h3'),
  },
  {
    id: 'fal-h3-max-turbo',
    label: 'H3 Max Turbo / fal.ai',
    tagline: '高速な動画生成。参考画像は開始フレーム1枚、音声つき',
    note: '人物の参照画像に対応',
    costTier: 'low',
    durations: [5, 8, 12, 15],
    supports1080p: true,
    supportsSquare: true,
    audio: 'always',
    resolutionDescriptions: {
      '720p': '768Pで生成、標準品質',
      '1080p': '768Pから1080Pに高精細化',
    },
    promptMaxChars: 50000,
    sample: sample('fal-h3-max-turbo'),
  },
  {
    id: 'fal-h3-max',
    label: 'H3 Max / fal.ai',
    tagline: '複数の参考画像を使った動画生成。最大15秒、音声つき',
    note: '人物の参照画像に対応',
    costTier: 'low',
    durations: [5, 8, 12, 15],
    supports1080p: true,
    supportsSquare: true,
    audio: 'always',
    resolutionDescriptions: {
      '720p': '768Pで生成、標準品質',
      '1080p': '768Pから1080Pに高精細化',
    },
    promptMaxChars: 50000,
    sample: sample('fal-h3-max'),
  },
  {
    id: 'gemini',
    label: 'Gemini',
    tagline: '指示への忠実さが持ち味。ただし消費クレジットが最も大きい',
    note: '日本語のプロンプトは自動で英訳されます',
    costTier: 'high',
    // provider(Veo)が受け付けるのは 4/6/8 秒のみ。以前ここが [5, 6, 8] だったため、
    // 5秒を選ぶと必ず「Unsupported output video duration 5 seconds」で失敗していた。
    // サーバー側の validateDurationForModel は上限しか見ないので素通りする。
    // 4秒は duration_seconds の in: リストに無いため、今は出していない。
    durations: [6, 8],
    supports1080p: true,
    supportsSquare: false,
    audio: 'optional',
    resolutionDescriptions: GENERIC_RESOLUTION_DESCRIPTIONS,
    promptMaxChars: null,
    sample: sample('gemini'),
  },
];

/**
 * Seedance 1.5 Pro（id: 'seedance'）は 2026-08-30 に選択肢から引退させた。
 * 生成基盤が 2.0 系とは別の旧エンドポイント（/video/generations、裸のモデルID
 * 'seedance-1-5-pro'）を叩いており、タスク作成の時点で失敗する＝選んでも生成できない。
 *
 * ただし VideoModelType からは外していない。過去に生成したジョブが
 * video_model: 'seedance' を保持しており、履歴表示（GeneratingVideoJobCard の
 * MODEL_LABELS）がその値を読むため。「新規に選ばせない」ことと
 * 「既存データを読める」ことは別の関心事として分けている。
 */

export const DEFAULT_VIDEO_MODEL: VideoModelType = 'seedance-2-mini';

/**
 * 実在の人物が写る参照画像を受け付けるモデル。Seedance が 4001 で拒否したときに
 * 「これで作り直す」と案内できる唯一の逃げ道なので、id を直書きせずここから引く。
 */
export const PERSON_REFERENCE_VIDEO_MODEL: VideoModelType = 'minimax-h3';

/**
 * 未知の ID でも落ちずに既定モデルへ倒す。URL クエリや保存済みの下書きなど、
 * カタログから消したモデル ID が後から流れ込む経路があるため。
 */
export const getVideoModelSpec = (model: VideoModelType): VideoModelSpec =>
  VIDEO_MODEL_CATALOG.find((spec) => spec.id === model)
  ?? VIDEO_MODEL_CATALOG.find((spec) => spec.id === DEFAULT_VIDEO_MODEL)!;

export const getDurationOptions = (
  model: VideoModelType
): Array<{ value: VideoDurationSeconds; label: string; description: string }> =>
  getVideoModelSpec(model).durations.map((value) => ({
    value,
    ...DURATION_LABELS[value],
  }));

export const getResolutionOptions = (
  model: VideoModelType
): Array<{ value: VideoResolution; label: string; description: string }> => {
  const spec = getVideoModelSpec(model);
  const resolutions: VideoResolution[] = spec.supports1080p ? ['720p', '1080p'] : ['720p'];

  return resolutions.map((value) => ({
    value,
    label: value === '720p' ? '720p (HD)' : '1080p (Full HD)',
    description: spec.resolutionDescriptions[value],
  }));
};

export const getAspectRatioOptions = (
  model: VideoModelType
): Array<{ value: VideoAspectRatio; label: string; description: string }> => {
  const spec = getVideoModelSpec(model);
  const ratios: VideoAspectRatio[] = spec.supportsSquare
    ? ['16:9', '9:16', '1:1']
    : ['16:9', '9:16'];

  return ratios.map((value) => ({ value, ...ASPECT_RATIO_LABELS[value] }));
};

/** モデルを切り替えたとき、新モデルで無効になる設定を補正する。 */
export const reconcileSettingsForModel = (
  model: VideoModelType,
  current: {
    durationSeconds: VideoDurationSeconds;
    resolution: VideoResolution;
    aspectRatio: VideoAspectRatio;
  }
): { durationSeconds: VideoDurationSeconds; resolution: VideoResolution; aspectRatio: VideoAspectRatio } => {
  const spec = getVideoModelSpec(model);

  return {
    // 列挙漏れを避けるため「許可集合に無ければ全モデル共通の 8 秒へ」で統一する。
    durationSeconds: spec.durations.includes(current.durationSeconds)
      ? current.durationSeconds
      : 8,
    // 1080p 非対応モデルに 1080p のまま切り替えると API に拒否される。
    resolution: !spec.supports1080p && current.resolution === '1080p' ? '720p' : current.resolution,
    aspectRatio: !spec.supportsSquare && current.aspectRatio === '1:1' ? '16:9' : current.aspectRatio,
  };
};

/** checkPromptLength の結果。UI はこれだけを見れば警告文と可否を決められる。 */
export interface PromptLengthCheck {
  /** 実際に送信される文字列の**文字数**（バイト数ではない）。 */
  count: number;
  /** モデルの上限。判明していなければ null。 */
  max: number | null;
  /** 上限超過。上限が無いモデルでは常に false。 */
  isOver: boolean;
  /**
   * 生成を止めるべきか。
   *
   * サーバー側は送信前に AI でプロンプトを要約して上限に収めるので、超過＝即失敗ではない。
   * ただし動画スタイル（テンプレート）を選んでいる間は要約がスキップされ、長いまま
   * プロバイダへ渡って必ず失敗する。その場合だけ止める。
   */
  blocksGeneration: boolean;
}

/**
 * プロンプトがモデルの文字数上限に収まっているか調べる。
 *
 * 文字数は `Array.from` で数える。JS の `String.length` は UTF-16 code unit 数なので、
 * 絵文字などのサロゲートペアを2文字と数えてしまい、API 側の数え方とずれる。
 */
export const checkPromptLength = (
  model: VideoModelType,
  prompt: string,
  options: { skipsOptimization: boolean }
): PromptLengthCheck => {
  const max = getVideoModelSpec(model).promptMaxChars;
  const count = Array.from(prompt).length;
  const isOver = max !== null && count > max;

  return {
    count,
    max,
    isOver,
    blocksGeneration: isOver && options.skipsOptimization,
  };
};
